import type { OperationVariables } from '@apollo/client'
import type { ApolloWrapInpRes } from '../apollo/apolloClient.client.js'

export function useApolloWrapLoadMore<T extends Record<string, unknown>, Vars extends OperationVariables, Ui, KKK>(
  inp: ApolloWrapInpRes<T, Vars, Ui>,
  actions: {
    onDone?: () => void
    ui: (result: T) => KKK | None
    variables: (input: { input: Vars; result: NN<KKK> }) => { input: Vars; isValid: boolean }
    result: (input: { prevFull: T; prev: KKK | None; next: KKK | None }) => T
  }
): () => NotVoid {
  return (): NotVoid => {
    const queryInit = inp.query()
    if (!queryInit) return null

    const prevInput = queryInit.query?.variables
    const curr = queryInit.query?.getCurrentResult()?.data
    const result = curr ? actions.ui(curr as T) : undefined

    if (!result || !prevInput) return null
    const variables = actions.variables({ input: prevInput, result })
    if (!variables.input || !variables.isValid) return null

    return queryInit
      .process(
        queryInit.query.fetchMore<T, Vars>({
          variables: variables.input,
          updateQuery: (prev, { fetchMoreResult }) => {
            return {
              ...prev,
              ...actions.result({
                prevFull: prev as T,
                next: actions.ui(fetchMoreResult as T),
                prev: actions.ui(prev as T),
              }),
            }
          },
        }),
        variables.input
      )
      ?.then((res) => {
        actions?.onDone?.()
        return res
      })
  }
}
